import type { TEnrichedPersonData } from "@/app/api/enrich/email/schema"
import { getAccountTrialStatus } from "@/lib/accountTrialState"
import type { TAccount, TUserStatus } from "@/schemas/user.schema"

export const POSTHOG_PENDING_SIGNUP_DISTINCT_ID_STORAGE_KEY = "ringio.posthog.pending_signup_distinct_id"
export const POSTHOG_SIGNUP_PAGE_LOADED_EVENT = "signup_page_loaded"
export const POSTHOG_USER_SIGNED_UP_EVENT = "user_signed_up"

interface StorageLike {
  getItem(key: string): string | null
  removeItem(key: string): void
  setItem(key: string, value: string): void
}

interface PostHogIdentityClient {
  alias(alias: string, original?: string): void
  get_distinct_id?(): string
  group(groupType: string, groupKey: string, properties?: Record<string, unknown>): void
  identify(distinctId: string, properties?: Record<string, unknown>): void
}

type SignupIdentityInput = {
  email: string
  plan: "solo" | "team"
  company: string
  emailMarketingOptIn?: boolean
  firstName?: string
  lastName?: string
  phone?: string
  signupFlow?: "legacy" | "email_first"
  companySize?: string
  companyReps?: string
  companyCountry?: string
  smsOptIn?: boolean
  signupExperimentKey?: string
  signupExperimentVariant?: string
}

function normalizeDistinctId(value: string | null | undefined) {
  const normalizedValue = value?.trim()

  return normalizedValue ? normalizedValue : null
}

function compactPostHogProperties(properties: Record<string, unknown>) {
  return Object.fromEntries(
    Object.entries(properties).filter(([, value]) => {
      if (value === undefined || value === null) {
        return false
      }

      if (typeof value === "string") {
        return value.trim().length > 0
      }

      if (Array.isArray(value)) {
        return value.length > 0
      }

      return true
    })
  )
}

function buildDisplayName(firstName: string | null | undefined, lastName: string | null | undefined) {
  const displayName = [firstName, lastName]
    .map((value) => value?.trim())
    .filter(Boolean)
    .join(" ")

  return displayName || undefined
}

export function normalizeSignupDistinctId(email: string | null | undefined) {
  const normalizedEmail = email?.trim().toLowerCase()

  return normalizedEmail ? normalizedEmail : null
}

export function getAliasSourceDistinctIds({ currentDistinctId, persistedSignupDistinctId, nextDistinctId }: { currentDistinctId?: string | null; persistedSignupDistinctId?: string | null; nextDistinctId: string }) {
  const normalizedNextDistinctId = normalizeDistinctId(nextDistinctId)

  if (!normalizedNextDistinctId) {
    return []
  }

  const aliasSourceIds = [persistedSignupDistinctId, currentDistinctId].map((distinctId) => normalizeDistinctId(distinctId)).filter((distinctId): distinctId is string => Boolean(distinctId && distinctId !== normalizedNextDistinctId))

  return [...new Set(aliasSourceIds)]
}

function getAccountTrialStatusProperty(account?: TAccount) {
  if (!account) {
    return undefined
  }

  return getAccountTrialStatus(account)
}

function getTrustHubProperties(account?: TAccount) {
  return {
    accountTrustHubCustomerProfileStatus: account?.trustHubStatuses?.customerProfileStatus,
    accountTrustHubSmsVerificationStatus: account?.trustHubStatuses?.smsVerificationStatus,
    accountTrustHubA2pProfileStatus: account?.trustHubStatuses?.a2pProfileStatus,
    accountTrustHubShakenStirStatus: account?.trustHubStatuses?.shakenStirStatus,
    accountTrustHubCnamStatus: account?.trustHubStatuses?.cnamStatus,
    accountTrustHubVoiceIntegrityStatus: account?.trustHubStatuses?.voiceIntegrityStatus,
    accountTrustHubBrandRegistrationStatus: account?.trustHubStatuses?.brandRegistrationStatus,
    accountTrustHubCampaignStatus: account?.trustHubStatuses?.campaignStatus,
  }
}

export function buildAuthenticatedPostHogPersonProperties(user: TUserStatus, account?: TAccount) {
  return compactPostHogProperties({
    firstName: user.name,
    lastName: user.lastName,
    displayName: buildDisplayName(user.name, user.lastName),
    email: user.email,
    companyName: user.companyName,
    account: user.accountId,
    ringioUserId: user.id,
    ringioAccountId: user.accountId,
    status: user.accountState,
    seats: user.accountSeats,
    signupChannel: user.accountSignupChannel,
    signupPlan: user.accountSignupPlan,
    crmSeats: user.crmSeats,
    smsOptIn: user.smsOptIn,
    emailMarketingOptIn: user.emailMarketingOptIn,
    userCreatedAt: user.dateCreated,
    userCallMinutesLast30Days: user.callMinutesLast30Days,
    userDialingSessionsLast30Days: user.dialingSessionsLast30Days,
    vmdropSetup: user.vmdropSetup,
    powerDialer: user.powerDialer,
    trialType: user.accountTrialType,
    trialActivated: user.accountTrialActivated,
    accountTrialStatus: getAccountTrialStatusProperty(account),
    accountInTrial: account?.inTrial,
    accountTrialExpired: account?.trialExpired,
    accountTrialConsumed: account?.trialConsumed,
    accountTrialDaysRemaining: account?.trialDaysRemaining,
    accountTrialMinutesRemaining: account?.trialMinutesRemaining,
    accountSoloFree: account?.isSoloFree,
    accountSoloTrialConsumed: account?.soloTrialConsumed,
    accountSoloTrialMinutesRemaining: account?.soloTrialMinutesRemaining,
    accountSmsEnabled: user.accountSmsEnabled,
    accountCallMinutesLast30Days: user.accountCallMinutesLast30Days,
    accountDialingSessionsLast30Days: user.accountDialingSessionsLast30Days,
    accountCrmId: account?.crm?.id,
    accountCrmAlias: account?.crm?.alias,
    accountCrmName: account?.crm?.name,
    accountPowerDialerEnabled: account?.powerDialerEnabled,
    accountTeamInvited: account?.teamInvited,
    accountTemplatesEnabled: account?.templatesEnabled,
    accountVideoCallEnabled: account?.videoCallEnabled,
    accountCustomCallerIdEnabled: account?.customCallerIdEnabled,
    accountMachineDetectionEnabled: account?.machineDetectionEnabled,
    accountEnableClickToCallOnlyInsideCrm: account?.enableClickToCallOnlyInsideCrm,
    accountTimezone: account?.timeZone,
    ...getTrustHubProperties(account),
    isOwner: user.isOwner,
    isAdmin: user.isAdmin,
    canAccessMonitor: user.canAccessMonitor,
  })
}

export function getAuthenticatedPostHogGroupKey(user: TUserStatus) {
  if (user.accountId > 0) {
    return user.accountId.toString()
  }

  return user.companyName || null
}

export function buildAuthenticatedPostHogGroupProperties(user: TUserStatus, account?: TAccount) {
  return compactPostHogProperties({
    name: user.companyName,
    id: user.accountId,
    ringioAccountId: user.accountId,
    status: user.accountState,
    seats: user.accountSeats,
    signupChannel: user.accountSignupChannel,
    signupPlan: user.accountSignupPlan,
    crmSeats: user.crmSeats,
    crmId: account?.crm?.id,
    crmAlias: account?.crm?.alias,
    crmName: account?.crm?.name,
    trialType: user.accountTrialType,
    trialActivated: user.accountTrialActivated,
    trialStatus: getAccountTrialStatusProperty(account),
    inTrial: account?.inTrial,
    trialExpired: account?.trialExpired,
    trialConsumed: account?.trialConsumed,
    trialDaysRemaining: account?.trialDaysRemaining,
    trialMinutesRemaining: account?.trialMinutesRemaining,
    soloFree: account?.isSoloFree,
    soloTrialConsumed: account?.soloTrialConsumed,
    soloTrialMinutesRemaining: account?.soloTrialMinutesRemaining,
    smsEnabled: user.accountSmsEnabled,
    callMinutesLast30Days: user.accountCallMinutesLast30Days,
    dialingSessionsLast30Days: user.accountDialingSessionsLast30Days,
    powerDialerEnabled: account?.powerDialerEnabled,
    teamInvited: account?.teamInvited,
    templatesEnabled: account?.templatesEnabled,
    videoCallEnabled: account?.videoCallEnabled,
    customCallerIdEnabled: account?.customCallerIdEnabled,
    machineDetectionEnabled: account?.machineDetectionEnabled,
    enableClickToCallOnlyInsideCrm: account?.enableClickToCallOnlyInsideCrm,
    timezone: account?.timeZone,
    ...getTrustHubProperties(account),
  })
}

export function getAuthenticatedPostHogIdentitySignature(user: TUserStatus, account?: TAccount) {
  return JSON.stringify({
    distinctId: user.id.toString(),
    person: buildAuthenticatedPostHogPersonProperties(user, account),
    groupKey: getAuthenticatedPostHogGroupKey(user),
    group: buildAuthenticatedPostHogGroupProperties(user, account),
  })
}

export function identifyAuthenticatedPostHogUser({ account, client, currentDistinctId, storage, user }: { account?: TAccount; client: PostHogIdentityClient; currentDistinctId?: string | null; storage?: StorageLike | null; user: TUserStatus }) {
  const nextDistinctId = user.id.toString()
  const aliasSourceIds = getAliasSourceDistinctIds({
    currentDistinctId: currentDistinctId ?? client.get_distinct_id?.(),
    persistedSignupDistinctId: readPendingSignupDistinctId(storage),
    nextDistinctId,
  })

  aliasSourceIds.forEach((aliasSourceId) => {
    client.alias(nextDistinctId, aliasSourceId)
  })

  client.identify(nextDistinctId, buildAuthenticatedPostHogPersonProperties(user, account))
  clearPendingSignupDistinctId(storage)

  const groupKey = getAuthenticatedPostHogGroupKey(user)
  if (groupKey) {
    client.group("company", groupKey, buildAuthenticatedPostHogGroupProperties(user, account))
  }
}

export function buildSignupPostHogPersonProperties(userData: SignupIdentityInput, enrichmentData?: TEnrichedPersonData | null) {
  return compactPostHogProperties({
    email: userData.email,
    firstName: userData.firstName,
    lastName: userData.lastName,
    displayName: buildDisplayName(userData.firstName, userData.lastName),
    phone: userData.phone,
    plan: userData.plan,
    trialType: userData.plan,
    companyName: userData.company,
    smsOptIn: userData.smsOptIn,
    emailMarketingOptIn: userData.emailMarketingOptIn,
    signupFlow: userData.signupFlow,
    ...(userData.companySize && { companySize: userData.companySize }),
    ...(userData.companyReps && { companyReps: userData.companyReps }),
    ...(userData.companyCountry && { companyCountry: userData.companyCountry }),
    signupExperimentKey: userData.signupExperimentKey,
    signupExperimentVariant: userData.signupExperimentVariant,
    ...(enrichmentData && {
      enriched: true,
      enrichedFirstName: enrichmentData.firstName,
      enrichedLastName: enrichmentData.lastName,
      enrichedPhone: enrichmentData.phone,
      enrichedTitle: enrichmentData.title,
      enrichedCompany: enrichmentData.company,
      enrichedPersonId: enrichmentData.apolloData?.personId,
      enrichedEmailStatus: enrichmentData.apolloData?.emailStatus,
      enrichedHeadline: enrichmentData.apolloData?.headline,
      enrichedTwitter: enrichmentData.apolloData?.twitterUrl,
      enrichedGithub: enrichmentData.apolloData?.githubUrl,
      enrichedFacebook: enrichmentData.apolloData?.facebookUrl,
      enrichedPhotoUrl: enrichmentData.apolloData?.photoUrl,
      enrichedPersonCity: enrichmentData.apolloData?.personCity,
      enrichedPersonState: enrichmentData.apolloData?.personState,
      enrichedPersonCountry: enrichmentData.apolloData?.personCountry,
      enrichedSeniority: enrichmentData.apolloData?.seniority,
      enrichedDepartments: enrichmentData.apolloData?.departments,
      enrichedFunctions: enrichmentData.apolloData?.functions,
      enrichedCompanyId: enrichmentData.apolloData?.companyId,
      enrichedCompanyLinkedin: enrichmentData.apolloData?.companyLinkedinUrl,
      enrichedCompanyWebsite: enrichmentData.apolloData?.companyWebsiteUrl,
      enrichedCompanyPhone: enrichmentData.apolloData?.companyPhone,
      enrichedCompanyDomain: enrichmentData.apolloData?.companyDomain,
      enrichedCompanyTwitter: enrichmentData.apolloData?.companyTwitterUrl,
      enrichedCompanyFacebook: enrichmentData.apolloData?.companyFacebookUrl,
      enrichedCompanyIndustries: enrichmentData.apolloData?.companyIndustries,
      enrichedCompanyStreetAddress: enrichmentData.apolloData?.companyStreetAddress,
      enrichedCompanyCity: enrichmentData.apolloData?.companyCity,
      enrichedCompanyState: enrichmentData.apolloData?.companyState,
      enrichedCompanyPostalCode: enrichmentData.apolloData?.companyPostalCode,
      enrichedCompanyCountryFull: enrichmentData.apolloData?.companyCountryFull,
      enrichedCompanyDescription: enrichmentData.apolloData?.companyDescription,
      enrichedCompanyFoundedYear: enrichmentData.apolloData?.companyFoundedYear,
      enrichedCompanyTotalFunding: enrichmentData.apolloData?.companyTotalFunding,
      enrichedCompanyLatestFundingStage: enrichmentData.apolloData?.companyLatestFundingStage,
      enrichedIndustry: enrichmentData.apolloData?.industry,
      enrichedEmployeeCount: enrichmentData.apolloData?.employeeCount,
      enrichedRevenue: enrichmentData.apolloData?.revenue,
      enrichedTechnologies: enrichmentData.apolloData?.technologies,
      enrichedLinkedin: enrichmentData.apolloData?.linkedinUrl,
    }),
  })
}

export function buildSignupPostHogEventProperties(userData: SignupIdentityInput, enrichmentData?: TEnrichedPersonData | null) {
  return compactPostHogProperties({
    email: userData.email,
    firstNameProvided: Boolean(userData.firstName),
    lastNameProvided: Boolean(userData.lastName),
    phoneProvided: Boolean(userData.phone),
    plan: userData.plan,
    trialType: userData.plan,
    companyName: userData.company,
    smsOptIn: userData.smsOptIn,
    emailMarketingOptIn: userData.emailMarketingOptIn,
    signupFlow: userData.signupFlow,
    ...(userData.companySize && { companySize: userData.companySize }),
    ...(userData.companyReps && { companyReps: userData.companyReps }),
    ...(userData.companyCountry && { companyCountry: userData.companyCountry }),
    signupExperimentKey: userData.signupExperimentKey,
    signupExperimentVariant: userData.signupExperimentVariant,
    ...(enrichmentData && {
      enriched: true,
      enrichedFirstName: Boolean(enrichmentData.firstName),
      enrichedLastName: Boolean(enrichmentData.lastName),
      enrichedPhone: Boolean(enrichmentData.phone),
      enrichedTitle: enrichmentData.title,
      enrichedCompany: enrichmentData.company,
      enrichedPersonId: Boolean(enrichmentData.apolloData?.personId),
      enrichedEmailStatus: Boolean(enrichmentData.apolloData?.emailStatus),
      enrichedHeadline: Boolean(enrichmentData.apolloData?.headline),
      enrichedTwitter: Boolean(enrichmentData.apolloData?.twitterUrl),
      enrichedGithub: Boolean(enrichmentData.apolloData?.githubUrl),
      enrichedFacebook: Boolean(enrichmentData.apolloData?.facebookUrl),
      enrichedPhotoUrl: Boolean(enrichmentData.apolloData?.photoUrl),
      enrichedPersonCity: Boolean(enrichmentData.apolloData?.personCity),
      enrichedPersonState: Boolean(enrichmentData.apolloData?.personState),
      enrichedPersonCountry: Boolean(enrichmentData.apolloData?.personCountry),
      enrichedSeniority: Boolean(enrichmentData.apolloData?.seniority),
      enrichedDepartments: Boolean(enrichmentData.apolloData?.departments?.length),
      enrichedFunctions: Boolean(enrichmentData.apolloData?.functions?.length),
      enrichedCompanyId: Boolean(enrichmentData.apolloData?.companyId),
      enrichedCompanyLinkedin: Boolean(enrichmentData.apolloData?.companyLinkedinUrl),
      enrichedCompanyWebsite: Boolean(enrichmentData.apolloData?.companyWebsiteUrl),
      enrichedCompanyPhone: Boolean(enrichmentData.apolloData?.companyPhone),
      enrichedCompanyDomain: Boolean(enrichmentData.apolloData?.companyDomain),
      enrichedCompanyTwitter: Boolean(enrichmentData.apolloData?.companyTwitterUrl),
      enrichedCompanyFacebook: Boolean(enrichmentData.apolloData?.companyFacebookUrl),
      enrichedCompanyIndustries: Boolean(enrichmentData.apolloData?.companyIndustries?.length),
      enrichedCompanyStreetAddress: Boolean(enrichmentData.apolloData?.companyStreetAddress),
      enrichedCompanyCity: Boolean(enrichmentData.apolloData?.companyCity),
      enrichedCompanyState: Boolean(enrichmentData.apolloData?.companyState),
      enrichedCompanyPostalCode: Boolean(enrichmentData.apolloData?.companyPostalCode),
      enrichedCompanyCountryFull: Boolean(enrichmentData.apolloData?.companyCountryFull),
      enrichedCompanyDescription: Boolean(enrichmentData.apolloData?.companyDescription),
      enrichedCompanyFoundedYear: Boolean(enrichmentData.apolloData?.companyFoundedYear),
      enrichedCompanyTotalFunding: Boolean(enrichmentData.apolloData?.companyTotalFunding),
      enrichedCompanyLatestFundingStage: Boolean(enrichmentData.apolloData?.companyLatestFundingStage),
      enrichedIndustry: enrichmentData.apolloData?.industry,
      enrichedEmployeeCount: enrichmentData.apolloData?.employeeCount,
      enrichedRevenue: enrichmentData.apolloData?.revenue,
      enrichedTechnologies: enrichmentData.apolloData?.technologies,
      enrichedLinkedin: Boolean(enrichmentData.apolloData?.linkedinUrl),
    }),
  })
}

export function readPendingSignupDistinctId(storage: StorageLike | null | undefined) {
  if (!storage) {
    return null
  }

  return normalizeSignupDistinctId(storage.getItem(POSTHOG_PENDING_SIGNUP_DISTINCT_ID_STORAGE_KEY))
}

export function persistPendingSignupDistinctId(storage: StorageLike | null | undefined, distinctId: string | null | undefined) {
  if (!storage) {
    return
  }

  const normalizedDistinctId = normalizeSignupDistinctId(distinctId)

  if (!normalizedDistinctId) {
    storage.removeItem(POSTHOG_PENDING_SIGNUP_DISTINCT_ID_STORAGE_KEY)
    return
  }

  storage.setItem(POSTHOG_PENDING_SIGNUP_DISTINCT_ID_STORAGE_KEY, normalizedDistinctId)
}

export function clearPendingSignupDistinctId(storage: StorageLike | null | undefined) {
  storage?.removeItem(POSTHOG_PENDING_SIGNUP_DISTINCT_ID_STORAGE_KEY)
}
